import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import logo from '../assets/images/9works_Logo/svg/9works_Original_Black.svg';

function TermsOfService() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="legal-page">
      <nav className="legal-nav">
        <div className="container">
          <div className="legal-nav-inner">
            <Link to="/" className="navbar-logo"><img src={logo} alt="9works" style={{ height: 36 }} /></Link>
            <Link to="/" className="btn btn-secondary" style={{ padding: '8px 20px', fontSize: 13 }}>Back to Home</Link>
          </div>
        </div>
      </nav>

      <div className="legal-content">
        <div className="container">
          <div className="legal-header">
            <h1>Terms of Service</h1>
            <p className="legal-effective">Effective Date: March 25, 2026 &nbsp;|&nbsp; Last Updated: March 25, 2026</p>
          </div>

          <div className="legal-body">
            <p>
              These Terms of Service ("Terms") govern your access to and use of the 9works platform,
              website (<strong>9works.in</strong>), mobile applications, and related services (collectively,
              the "Service"), operated by ninea.ai ("Company," "we," "us," or "our").
            </p>
            <p>
              By creating an account, accessing, or using the Service, you agree to be bound by these Terms.
              If you do not agree to these Terms, do not use the Service. If you are using the Service on
              behalf of an organization, you represent that you have authority to bind that organization to these Terms.
            </p>

            <h2>1. Definitions</h2>
            <ul>
              <li><strong>"User"</strong> means any individual who accesses or uses the Service, including Staff Members, Organization Administrators, and Platform Owners.</li>
              <li><strong>"Organization"</strong> means the business entity that subscribes to the Service for workforce management purposes.</li>
              <li><strong>"Staff Member"</strong> means an individual added to the Service by an Organization Administrator for scheduling, attendance, and related purposes.</li>
              <li><strong>"Organization Administrator"</strong> means a User authorized by the Organization to manage settings, staff, rosters, and related configurations.</li>
              <li><strong>"Platform Owner"</strong> means the super-administrator who manages Organizations, subscriptions, and system-wide configurations.</li>
              <li><strong>"Content"</strong> means any data, text, files, documents, or materials uploaded to or generated through the Service.</li>
              <li><strong>"Subscription Plan"</strong> means the pricing tier and feature set selected by the Organization.</li>
            </ul>

            <h2>2. Account Registration and Eligibility</h2>
            <h3>2.1 Eligibility</h3>
            <p>
              You must be at least 16 years of age to use the Service. By using the Service, you represent
              that you meet this requirement and have the legal capacity to enter into these Terms.
            </p>
            <h3>2.2 Account Security</h3>
            <ul>
              <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
              <li>You agree to notify us immediately of any unauthorized use of your account.</li>
              <li>You are responsible for all activities that occur under your account.</li>
              <li>We reserve the right to suspend or terminate accounts that violate these Terms.</li>
            </ul>
            <h3>2.3 Accurate Information</h3>
            <p>
              You agree to provide accurate, current, and complete information during registration and to
              update such information to keep it accurate, current, and complete.
            </p>

            <h2>3. Use of the Service</h2>
            <h3>3.1 Permitted Use</h3>
            <p>
              The Service is provided for lawful workforce management purposes, including but not limited to:
              shift scheduling, attendance tracking, leave management, employee management, billing, and
              related organizational operations.
            </p>
            <h3>3.2 Prohibited Use</h3>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Service for any unlawful purpose or in violation of any applicable laws or regulations.</li>
              <li>Attempt to gain unauthorized access to any part of the Service, other accounts, or systems.</li>
              <li>Reverse engineer, decompile, disassemble, or attempt to derive the source code of the Service.</li>
              <li>Use the Service to transmit viruses, malware, or other harmful code.</li>
              <li>Interfere with or disrupt the integrity or performance of the Service.</li>
              <li>Use automated tools (bots, scrapers, crawlers) to access the Service without our prior written consent.</li>
              <li>Sublicense, resell, or redistribute the Service without our authorization.</li>
              <li>Use the Service to collect, store, or process personal data in violation of applicable data protection laws.</li>
              <li>Upload or transmit content that is defamatory, obscene, fraudulent, or infringes intellectual property rights.</li>
            </ul>

            <h2>4. Subscription Plans and Billing</h2>
            <h3>4.1 Plans and Pricing</h3>
            <p>
              The Service is offered under various subscription plans with different feature sets and pricing.
              Current pricing is available on our website or upon request. We reserve the right to modify
              pricing with 30 days' advance notice.
            </p>
            <h3>4.2 Payment Terms</h3>
            <ul>
              <li>Subscription fees are billed in advance on a monthly or annual basis, depending on the selected plan.</li>
              <li>All fees are non-refundable unless otherwise stated or required by applicable law.</li>
              <li>You are responsible for all applicable taxes.</li>
              <li>Failure to pay may result in suspension or termination of the Service.</li>
            </ul>
            <h3>4.3 Free Trial</h3>
            <p>
              We may offer free trial periods at our discretion. At the end of a trial, you will need to
              subscribe to a paid plan to continue using the Service. We reserve the right to modify or
              discontinue trial offers at any time.
            </p>

            <h2>5. Intellectual Property</h2>
            <h3>5.1 Our Intellectual Property</h3>
            <p>
              The Service, including its design, code, features, branding, logos, documentation, and all
              related intellectual property, is owned by ninea.ai and protected by copyright, trademark,
              and other intellectual property laws. Nothing in these Terms grants you any right, title, or
              interest in our intellectual property.
            </p>
            <h3>5.2 Your Content</h3>
            <p>
              You retain ownership of the Content you upload to the Service. By uploading Content, you grant
              us a limited, non-exclusive license to use, process, store, and display that Content solely for
              the purpose of providing the Service. This license terminates when you delete your Content or
              close your account.
            </p>
            <h3>5.3 Feedback</h3>
            <p>
              Any feedback, suggestions, or ideas you provide regarding the Service may be used by us without
              any obligation to compensate you or maintain confidentiality.
            </p>

            <h2>6. Data Protection and Privacy</h2>
            <p>
              Your privacy is important to us. Our collection and use of personal information is described
              in our <Link to="/privacy-policy">Privacy Policy</Link>, which is incorporated into these Terms
              by reference.
            </p>
            <h3>6.1 Data Processing</h3>
            <p>
              For Organizations subject to GDPR or similar data protection regulations, we act as a Data
              Processor on behalf of the Organization (Data Controller). Organizations are responsible for
              ensuring they have a lawful basis for processing the personal data of their staff members
              through the Service.
            </p>
            <h3>6.2 Data Responsibility</h3>
            <p>
              Organization Administrators are responsible for the accuracy and lawfulness of the data they
              input into the Service, including employee information, schedules, and organizational data.
            </p>

            <h2>7. Service Availability and Support</h2>
            <h3>7.1 Availability</h3>
            <p>
              We strive to maintain 99.9% uptime but do not guarantee uninterrupted access. The Service may
              be temporarily unavailable due to maintenance, updates, or circumstances beyond our control.
              We will provide reasonable notice for planned maintenance when possible.
            </p>
            <h3>7.2 Support</h3>
            <p>
              We provide customer support via email and in-app channels. Response times and support levels
              may vary based on your subscription plan. Critical issues affecting service availability are
              prioritized regardless of plan.
            </p>
            <h3>7.3 Modifications to the Service</h3>
            <p>
              We reserve the right to modify, update, or discontinue any part of the Service at any time.
              For material changes that negatively impact your use, we will provide at least 30 days' notice.
            </p>

            <h2>8. Termination</h2>
            <h3>8.1 Termination by You</h3>
            <p>
              You may terminate your account at any time by contacting us or through the account settings.
              Organization Administrators may cancel their organization's subscription. Upon termination,
              your right to use the Service ceases immediately.
            </p>
            <h3>8.2 Termination by Us</h3>
            <p>We may suspend or terminate your access to the Service if:</p>
            <ul>
              <li>You breach any provision of these Terms.</li>
              <li>Your subscription payment fails and remains unresolved for 15 days.</li>
              <li>Your use of the Service poses a security risk or may adversely impact other users.</li>
              <li>Required by law or regulatory authority.</li>
            </ul>
            <h3>8.3 Effect of Termination</h3>
            <p>
              Upon termination, we will make your data available for export for 30 days. After this period,
              we will delete your data in accordance with our Privacy Policy. Provisions that by their nature
              should survive termination (including intellectual property, limitation of liability, and
              indemnification) will survive.
            </p>

            <h2>9. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
              EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY,
              FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
            </p>
            <p>We do not warrant that:</p>
            <ul>
              <li>The Service will be uninterrupted, secure, or error-free.</li>
              <li>Results obtained from the Service will be accurate or reliable.</li>
              <li>The Service will meet your specific requirements.</li>
              <li>Any defects in the Service will be corrected.</li>
            </ul>

            <h2>10. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL THE COMPANY, ITS
              DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
              SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS,
              DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR IN CONNECTION WITH:
            </p>
            <ul>
              <li>Your access to or use of (or inability to access or use) the Service.</li>
              <li>Any conduct or content of any third party on the Service.</li>
              <li>Unauthorized access, use, or alteration of your transmissions or content.</li>
              <li>Any scheduling errors, missed shifts, or workforce management decisions made using the Service.</li>
            </ul>
            <p>
              OUR TOTAL AGGREGATE LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY YOU TO US IN THE TWELVE (12)
              MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
            </p>

            <h2>11. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless the Company and its officers, directors,
              employees, and agents from any claims, damages, losses, liabilities, and expenses (including
              reasonable legal fees) arising out of or related to:
            </p>
            <ul>
              <li>Your use of the Service.</li>
              <li>Your violation of these Terms.</li>
              <li>Your violation of any rights of a third party.</li>
              <li>Content you upload or transmit through the Service.</li>
            </ul>

            <h2>12. Governing Law and Dispute Resolution</h2>
            <h3>12.1 Governing Law</h3>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of India,
              without regard to its conflict of law provisions.
            </p>
            <h3>12.2 Dispute Resolution</h3>
            <p>
              Any dispute arising out of or relating to these Terms shall first be attempted to be resolved
              through good-faith negotiation. If negotiation fails, the dispute shall be submitted to binding
              arbitration in accordance with the Arbitration and Conciliation Act, 1996 of India. The
              arbitration shall be conducted in English.
            </p>
            <h3>12.3 Jurisdiction</h3>
            <p>
              For any matters not subject to arbitration, you consent to the exclusive jurisdiction of the
              courts located in India.
            </p>

            <h2>13. General Provisions</h2>
            <ul>
              <li><strong>Entire Agreement:</strong> These Terms, together with the Privacy Policy, constitute the entire agreement between you and us regarding the Service.</li>
              <li><strong>Severability:</strong> If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full force and effect.</li>
              <li><strong>Waiver:</strong> Our failure to enforce any right or provision of these Terms does not constitute a waiver of that right or provision.</li>
              <li><strong>Assignment:</strong> You may not assign or transfer these Terms without our prior written consent. We may assign these Terms without restriction.</li>
              <li><strong>Force Majeure:</strong> We shall not be liable for any failure to perform due to circumstances beyond our reasonable control, including natural disasters, wars, pandemics, government actions, or internet disruptions.</li>
              <li><strong>Notices:</strong> We may provide notices via email, in-app notification, or by posting on the Service. Notices to us must be sent to legal@ninea.ai.</li>
            </ul>

            <h2>14. Changes to These Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify you of material changes
              at least 30 days before they take effect by posting the updated Terms on this page and updating
              the "Last Updated" date. For significant changes, we may also notify you via email or in-app
              notification. Your continued use of the Service after the effective date of changes constitutes
              acceptance of the revised Terms.
            </p>

            <h2>15. Contact Us</h2>
            <p>If you have any questions about these Terms, please contact us:</p>
            <div className="legal-contact-box">
              <p><strong>9works — A product by ninea.ai</strong></p>
              <p>Email: <a href="mailto:legal@ninea.ai">legal@ninea.ai</a></p>
              <p>Website: <a href="https://9works.in">9works.in</a></p>
              <p>Parent Company: <a href="https://www.ninea.ai" target="_blank" rel="noopener noreferrer">ninea.ai</a></p>
            </div>
          </div>
        </div>
      </div>

      <div className="legal-footer">
        <div className="container">
          <p>&copy; {new Date().getFullYear()} 9works. A product by <a href="https://www.ninea.ai" target="_blank" rel="noopener noreferrer">ninea.ai</a>. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}

export default TermsOfService;
